import { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { RegistrationContext } from '~/context/registration';
import { useToasts } from 'react-toast-notifications';

import { setCampaignContribution } from '~/services/registration';
import { identityOptions } from '~/shared/config';

import {
    TextInput,
    SelectInput,
    currencyConfig,
} from '~/shared/components/form/Inputs';
import { ProgressBar } from '../ProgressBar';
import IntlCurrencyInput from 'react-intl-currency-input';
import {
    Container,
    Button,
    Card,
    CardBody,
    Col,
    Row,
    Form,
    FormGroup,
    Input,
    Label,
} from 'reactstrap';

import { Loading } from '~/shared/components/Loading';

import moment from 'moment';

const booleanOptions = [
    {
        label: 'Yes',
        value: true,
    },
    {
        label: 'No',
        value: false,
    },
];

export const Contribution = ({}: any) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [isSelfCampaign, setIsSelfCampaign] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [{ campaignRequest }, registrationDispatch] =
        useContext(RegistrationContext);
    const today = moment().format('Y-MM-DD');
    const {
        register,
        handleSubmit,
        watch,
        errors,
        control,
        reset,
        setValue,
        getValues,
        formState: { isSubmitting, isValid },
    } = useForm({
        fin_num_in_household: '',
        fin_is_single_parent_household: '',
        fin_is_irs_dependent: '',
        fin_monthly_wages: 0,
        fin_soc_sec_income: 0,
        fin_other_income: 0,
        fin_tot_monthly_income: '',
        fin_anl_household_income: '',
        fin_other_comments: '',
        fin_certify_identity: '',
        fin_signature: '',
        fin_signature_date: '',
    });

    const wageIncome = watch('fin_monthly_wages') || 0;
    const socIncome = watch('fin_soc_sec_income') || 0;
    const otherIncome = watch('fin_other_income') || 0;

    // Round to 2 decimal places to avoid floating-point precision errors
    const monthlyIncome = Math.round((wageIncome + socIncome + otherIncome) * 100) / 100;
    const annualIncome = Math.round(monthlyIncome * 12 * 100) / 100;

    const { addToast } = useToasts();
    const history = useHistory();

    useEffect(() => {
        setValue('fin_signature_date', today);
    }, []);

    useEffect(() => {
        setValue('fin_tot_monthly_income', monthlyIncome);
    }, [monthlyIncome]);

    useEffect(() => {
        setValue('fin_anl_household_income', annualIncome);
    }, [annualIncome]);

    const onSubmit = async (data) => {
        try {
            let payload = data;

            const resp = await setCampaignContribution(payload);
            console.log('resp: ', resp);

            registrationDispatch({
                type: 'set_application',
                application: resp,
            });
            history.push('/get-started/financials/insurance');
        } catch (e) {
            console.log('e: ', e);
        }
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <Container>
            <ProgressBar selectedStep='financials' />

            <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className='justify-content-center py-5'>
                    <Col md='6'>
                        <Card
                            className='box-shadow my-5'
                            style={{
                                maxWidth: '800px',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                            }}
                        >
                            <CardBody>
                                <h3>Financial Information</h3>
                                <p>
                                    By collecting some of the tax information
                                    for your household, we are able to better
                                    gauge what your need is. A household usually
                                    includes the tax filer, their spouse if they
                                    have one, and their tax dependents.
                                </p>

                                <TextInput
                                    name='fin_num_in_household'
                                    label='Number in household?'
                                    type='number'
                                    control={control}
                                    errors={
                                        errors?.fin_num_in_household?.message
                                    }
                                    required={true}
                                    ariaRequired={true}
                                />
                                <Label>
                                    Is this a single parent household? *
                                </Label>
                                <FormGroup check inline className='mb-3'>
                                    {booleanOptions.map((opt, i) => {
                                        return (
                                            <div key={i}>
                                                <Label check>
                                                    <Input
                                                        type='radio'
                                                        value={opt.value}
                                                        onClick={() =>
                                                            setValue(
                                                                'fin_is_single_parent_household',
                                                                opt.value
                                                            )
                                                        }
                                                        name='fin_is_single_parent_household'
                                                    />
                                                    {opt.label}
                                                </Label>
                                            </div>
                                        );
                                    })}
                                </FormGroup>
                                <Label>
                                    Does a parent, spouse or other relative
                                    claim you as a dependent on their tax
                                    return? *
                                </Label>
                                <FormGroup check inline className='mb-3'>
                                    {booleanOptions.map((opt, i) => {
                                        return (
                                            <div key={i}>
                                                <Label check>
                                                    <Input
                                                        type='radio'
                                                        value={opt.value}
                                                        onClick={() =>
                                                            setValue(
                                                                'fin_is_irs_dependent',
                                                                opt.value
                                                            )
                                                        }
                                                        name='fin_is_irs_dependent'
                                                    />
                                                    {opt.label}
                                                </Label>
                                            </div>
                                        );
                                    })}
                                </FormGroup>
                                <p>
                                    Use the following to estimate your household
                                    total monthly income. Household income is
                                    the combined gross income of all members of
                                    a household who are 15 years or older,
                                    either related or non-related.
                                </p>

                                <FormGroup>
                                    <Label>Monthly Salary/Wages/Tips</Label>
                                    <IntlCurrencyInput
                                        onChange={(e, val, masked) => {
                                            setValue('fin_monthly_wages', val);
                                        }}
                                        className='form-control'
                                        config={currencyConfig}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>Social Security</Label>
                                    <IntlCurrencyInput
                                        onChange={(e, val, masked) => {
                                            setValue('fin_soc_sec_income', val);
                                        }}
                                        className='form-control'
                                        config={currencyConfig}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>
                                        Other Income (such as capital gains,
                                        dividends, interest, rents, pensions,
                                        alimony)
                                    </Label>
                                    <IntlCurrencyInput
                                        onChange={(e, val, masked) => {
                                            setValue('fin_other_income', val);
                                        }}
                                        className='form-control'
                                        config={currencyConfig}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>
                                        Total monthly income (sum of the above)
                                    </Label>
                                    <IntlCurrencyInput
                                        value={monthlyIncome}
                                        className='form-control'
                                        config={currencyConfig}
                                        readOnly={true}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>
                                        Annual household income (monthly income
                                        x 12)
                                    </Label>
                                    <IntlCurrencyInput
                                        value={annualIncome}
                                        className='form-control'
                                        config={currencyConfig}
                                        readOnly={true}
                                    />
                                </FormGroup>
                                <TextInput
                                    name='fin_other_comments'
                                    label='Other/additional comments'
                                    type='textarea'
                                    control={control}
                                    errors={errors?.fin_other_comments?.message}
                                />

                                <hr />

                                <p>
                                    I understand that information in this
                                    application will be used by Help Hope Live
                                    to verify financial need, as required by the
                                    IRS. I guarantee that the above information
                                    is true to the best of my knowledge.
                                </p>

                                <SelectInput
                                    control={control}
                                    required={true}
                                    ariaRequired={true}
                                    name='fin_certify_identity'
                                    label='I certify that I am the:'
                                    defaultLabel='Select'
                                    options={identityOptions}
                                    errors={
                                        errors?.fin_certify_identity?.message
                                    }
                                />

                                <Row>
                                    <Col>
                                        <TextInput
                                            name='fin_signature'
                                            label='Name'
                                            control={control}
                                            errors={
                                                errors?.fin_signature?.message
                                            }
                                            required={true}
                                            ariaRequired={true}
                                        />
                                    </Col>
                                    <Col>
                                        <TextInput
                                            name='fin_signature_date'
                                            label='Date'
                                            control={control}
                                            type='date'
                                            errors={
                                                errors?.fin_signature_date
                                                    ?.message
                                            }
                                            required={true}
                                            ariaRequired={true}
                                        />
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <div className='text-center mb-5'>
                    <Button
                        color='outline-dark'
                        disabled={isSubmitting}
                        onClick={() =>
                            history.push('/get-started/financials/cost')
                        }
                    >
                        Back
                    </Button>
                    <Button
                        color='primary'
                        type='submit'
                        disabled={!isValid || isSubmitting}
                    >
                        {isSubmitting ? `Saving...` : `Next`}
                    </Button>

                    <div className='mt-4'>
                        <Button 
                            color='link'
                            onClick={() => history.push('/get-started/community')}
                        >
                            Skip for Now
                        </Button>
                        <p>
                            You will be required to provide this information in
                            order to receive any funds raised for this campaign.
                        </p>
                    </div>
                </div>
            </Form>
        </Container>
    );
};
